import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import type { SnakeCase } from "type-fest";
import type { ZodType } from "zod";
import z from "zod";

import { buildInvalidError, type InvalidError } from "@thalamos/common";

import { SUPPLEMENT_KEYWORD } from "./authoring.js";
import {
  JsonValueSchema,
  toResponseRows,
  type JsonValue,
  type SubmissionDocumentUnbranded,
} from "./document.js";
import { isNotAnswered } from "./notAnswered.js";
import {
  closedValuesOf,
  type PublishedInstrumentSchema,
} from "./publishedSchema.js";
import { pointerFor } from "./validate.js";

/** The instrument revision a canonical text is the meaning of, named as an assessment pins one. */
export type MeaningSourceUnbranded = {
  name: string;
  digest: string;
};

/** What made a machine translation, as its collector identifies it: opaque to the core, which keeps it. */
export type TranslatorUnbranded = {
  service: string;
  version: string;
  /**
   * When the translation was made, which for one stored and shown again may
   * be long before this respondent saw it: a UTC ISO 8601 instant.
   */
  translatedAt: string;
};

/**
 * Where a text in words came from: the instrument's meaning word for word,
 * the collector's own wording, or a machine translation of another text.
 */
export type TextOriginUnbranded =
  | { kind: "canonical"; from: MeaningSourceUnbranded }
  | { kind: "collector" }
  | {
      kind: "machine-translated";
      from: PresentedTextUnbranded;
      by: TranslatorUnbranded;
    };

/**
 * One text exactly as a respondent was shown it: words, with their language
 * and where they came from, or an instrument's bare key or value shown where
 * there was no meaning to show, which is in no language.
 */
export type PresentedTextUnbranded =
  | { text: string; language: string; origin: TextOriginUnbranded }
  | { text: string; origin: { kind: "identifier" } };

/**
 * Where a note sits: before or after one part of its item (the question's
 * title and description, or its answers), listed in the order a respondent
 * reads them. Attached to a part rather than a gap, since a collector may draw
 * the parts apart, and in order rather than above or below, since a voice
 * collector has an order but no "above".
 */
export const notePlacements = [
  "before_question",
  "after_question",
  "before_answer",
  "after_answer",
] as const;
notePlacements satisfies readonly SnakeCase<(typeof notePlacements)[number]>[];

export type NotePlacementUnbranded = (typeof notePlacements)[number];

/**
 * What a framing text is to the item it frames: its group's heading, as the
 * section's title or description, or a note the collector put beside it,
 * such as a banner.
 */
export type FramingRoleUnbranded =
  "section_title" | "section_description" | "note";

/**
 * A text framing an item, with its role and, for a note, where it sat, so a
 * reader can lay the form out again without knowing the collector. Framing is
 * listed in the order read, but the question and its answers are fields of
 * their own, so only a placement says which of them a note sat beside.
 */
export type FramingTextUnbranded = PresentedTextUnbranded &
  (
    | { role: Exclude<FramingRoleUnbranded, "note"> }
    | {
        role: "note";
        /** Where the note sat; absent where the sender does not know, or did not honour one. */
        placement?: NotePlacementUnbranded;
      }
  );

/**
 * Who insisted on an answer, if anyone: the instrument, or the collector where
 * the instrument would have let it be left out.
 *
 * What was enforced, not how it was shown: a respondent meets a requirement
 * by being stopped without it, whether or not anything marked it.
 * Any marking is the collector's to record.
 */
export type RequiredByUnbranded = "no" | "by_instrument" | "by_collector";

/** One answer a closed item offered, whether or not it was chosen. */
export type PresentedAnswerUnbranded = {
  value: string | number;
  title: PresentedTextUnbranded;
  description?: PresentedTextUnbranded;
};

/**
 * What a respondent was shown for one item: each of its meaning's texts as
 * the one text shown, and who required an answer.
 *
 * A supplement, such as a comment, has an entry of its own, its title the
 * label it was shown with, straight after its item's entry or another
 * supplement to that item.
 */
export type PresentedItemUnbranded = {
  at: { group: string; item: string };
  /** Whether the item had to be answered, given its group was. */
  required: RequiredByUnbranded;
  /** Whether the item's group had to be answered at all. */
  groupRequired: RequiredByUnbranded;
  /**
   * The text around the item that bears on what it means, such as its
   * group's heading and instructions, as the sender judged it to apply, each
   * with the part it plays.
   */
  framing: FramingTextUnbranded[];
  title: PresentedTextUnbranded;
  description?: PresentedTextUnbranded;
  /** Every answer a closed item offered, chosen or not. */
  answers?: PresentedAnswerUnbranded[];
  /**
   * Anything more the collector records about the item, in its own
   * vocabulary, such as the control it asked for: opaque to the core.
   */
  collector?: Readonly<Record<string, JsonValue>>;
};

/**
 * Evidence of what a respondent was shown, sent beside a submission's
 * document: the sender's attestation, which the core keeps but cannot verify.
 *
 * A submission need not carry one: where answers were collected in another
 * system, nobody here was shown anything.
 */
export type PresentationUnbranded = {
  /**
   * The language the respondent asked to be shown, which a text need not be
   * in where the collector had nothing in it to show.
   */
  requestedLanguage: string;
  /**
   * Every item shown, answered or not, a supplement included, in the order the
   * form laid them out, as the form stood when the document was submitted.
   * The order is the layout's, not the order the respondent answered in.
   */
  items: PresentedItemUnbranded[];
};

const LanguageTagSchema = z.string().min(1);

const MeaningSourceSchema = z.strictObject({
  name: z.string().min(1),
  digest: z.string().min(1),
}) satisfies ZodType<MeaningSourceUnbranded>;

const TranslatorSchema = z.strictObject({
  service: z.string().min(1),
  version: z.string().min(1),
  translatedAt: z.iso.datetime(),
}) satisfies ZodType<TranslatorUnbranded>;

// Lazy, and typed by hand, because a machine translation's source is itself a
// presented text.
const TextOriginSchema: ZodType<TextOriginUnbranded> = z.lazy(() =>
  z.discriminatedUnion("kind", [
    z.strictObject({
      kind: z.literal("canonical"),
      from: MeaningSourceSchema,
    }),
    z.strictObject({ kind: z.literal("collector") }),
    z.strictObject({
      kind: z.literal("machine-translated"),
      from: PresentedTextSchema,
      by: TranslatorSchema,
    }),
  ]),
);

/** The fields of each kind of presented text, shared by a framing text, which adds its role. */
const presentedTextShapes = {
  worded: {
    text: z.string(),
    language: LanguageTagSchema,
    origin: TextOriginSchema,
  },
  identifier: {
    text: z.string(),
    origin: z.strictObject({ kind: z.literal("identifier") }),
  },
};

const PresentedTextSchema: ZodType<PresentedTextUnbranded> = z.union([
  z.strictObject(presentedTextShapes.worded),
  z.strictObject(presentedTextShapes.identifier),
]);

const framingShapes = {
  section: {
    role: z.enum(["section_title", "section_description"]) satisfies ZodType<
      SnakeCase<Exclude<FramingRoleUnbranded, "note">>
    >,
  },
  note: {
    role: z.literal("note"),
    placement: z.enum(notePlacements).optional(),
  },
};

const FramingTextSchema = z.union([
  z.strictObject({ ...presentedTextShapes.worded, ...framingShapes.section }),
  z.strictObject({ ...presentedTextShapes.worded, ...framingShapes.note }),
  z.strictObject({
    ...presentedTextShapes.identifier,
    ...framingShapes.section,
  }),
  z.strictObject({ ...presentedTextShapes.identifier, ...framingShapes.note }),
]) satisfies ZodType<FramingTextUnbranded>;

const RequiredBySchema = z.enum([
  "no",
  "by_instrument",
  "by_collector",
]) satisfies ZodType<SnakeCase<RequiredByUnbranded>>;

const PresentedItemSchema = z.strictObject({
  at: z.strictObject({ group: z.string(), item: z.string() }),
  required: RequiredBySchema,
  groupRequired: RequiredBySchema,
  framing: z.array(FramingTextSchema),
  title: PresentedTextSchema,
  description: PresentedTextSchema.optional(),
  answers: z
    .array(
      z.strictObject({
        value: z.union([z.string(), z.number()]),
        title: PresentedTextSchema,
        description: PresentedTextSchema.optional(),
      }),
    )
    .optional(),
  collector: z.record(z.string(), JsonValueSchema).optional(),
}) satisfies ZodType<PresentedItemUnbranded>;

/**
 * The evidence's shape, and nothing about what a particular instrument asks:
 * whether it can be attached to the pinned instrument is
 * `presentationIssuesIn`'s to say.
 *
 * Strict throughout, so a field the sender misnames is refused rather than
 * dropped from the record without anyone knowing.
 */
export const PresentationSchema = z.strictObject({
  requestedLanguage: LanguageTagSchema,
  items: z.array(PresentedItemSchema),
}) satisfies ZodType<PresentationUnbranded>;

/**
 * An item's own schema and its group's, or `undefined` where the instrument
 * does not ask it.
 */
const askedAt = (
  schema: PublishedInstrumentSchema,
  group: string,
  item: string,
) => {
  // `Object.hasOwn` rather than indexing alone, which answers for inherited
  // members: an entry at "toString" would otherwise find something.
  const groupSchema = Object.hasOwn(schema.properties, group)
    ? schema.properties[group]
    : undefined;
  const itemSchema =
    groupSchema !== undefined && Object.hasOwn(groupSchema.properties, item)
      ? groupSchema.properties[item]
      : undefined;

  return groupSchema === undefined || itemSchema === undefined
    ? undefined
    : { groupSchema, itemSchema };
};

/**
 * Where a claim about who required something contradicts the instrument: it
 * says the instrument required what it lets be left out, or the reverse.
 * Only a collector's own requirement is left unchecked.
 */
const requirementIssuesIn = (
  where: string,
  claimed: RequiredByUnbranded,
  byInstrument: boolean,
): string[] => {
  if (byInstrument && claimed !== "by_instrument") {
    return [`${where}: the instrument requires it, so it is "by_instrument"`];
  }

  return !byInstrument && claimed === "by_instrument"
    ? [`${where}: the instrument does not require it`]
    : [];
};

/**
 * What is wrong with one entry's answers, given the values its item allows
 * and the response the document gives it: a closed set must list what it
 * offered, and a response chosen from outside the answers the entry says were
 * offered contradicts it, as an answer to an item with no entry does.
 */
const answerIssuesIn = (
  at: string,
  answers: readonly PresentedAnswerUnbranded[] | undefined,
  allowed: readonly unknown[] | undefined,
  /** `undefined` where the document leaves the item unanswered. */
  response: JsonValue | undefined,
): string[] => {
  if (allowed === undefined) {
    return answers === undefined
      ? []
      : [`${at}/answers: the item is not a closed set, so offers no answers`];
  }

  if (answers === undefined) {
    return [
      `${at}/answers: the item is a closed set, so must list the answers it offered`,
    ];
  }

  const offered = answers.flatMap(({ value }, index) => {
    const where = `${at}/answers/${String(index)}/value`;
    const spelt = JSON.stringify(value);

    if (!allowed.includes(value)) {
      return [`${where}: ${spelt} is not an answer the item allows`];
    }

    return answers.findIndex((answer) => answer.value === value) < index
      ? [`${where}: ${spelt} is listed more than once`]
      : [];
  });
  // An item sent not answered chose none of the answers offered.
  const chosen =
    response === undefined ||
    isNotAnswered(response) ||
    answers.some(({ value }) => value === response)
      ? []
      : [
          `${at}/answers: ${JSON.stringify(response)} is answered, but not among the answers offered`,
        ];

  return [...offered, ...chosen];
};

/**
 * Everything that stops evidence being attached to a submission of the
 * pinned instrument, by JSON pointer into the evidence.
 *
 * A supplement is entered straight after the item it adds to, or after another
 * supplement to that item, so a reader meets the two together.
 *
 * Never judges the words. Nor does it require every allowed answer to have
 * been offered, since a collector may offer fewer than the instrument allows.
 */
export const presentationIssuesIn = ({
  schema,
  document,
  presentation,
}: {
  schema: PublishedInstrumentSchema;
  document: SubmissionDocumentUnbranded;
  presentation: PresentationUnbranded;
}): string[] => {
  const entered = new Set<string>();
  // The item whose supplements may be entered next.
  let lastNonSupplement: string | undefined;
  // Where the instrument leaves a group optional, only the evidence says
  // whether it was required, so the first entry's claim binds the rest.
  const optionalGroupClaims = new Map<
    string,
    { at: string; claimed: RequiredByUnbranded }
  >();
  const responses = new Map(
    toResponseRows(document).map(({ group, item, response }) => [
      pointerFor([group, item]),
      response,
    ]),
  );

  const entryIssues = presentation.items.flatMap((entry, index) => {
    const at = `/items/${String(index)}`;
    const { group, item } = entry.at;
    const itemPointer = pointerFor([group, item]);
    const asked = askedAt(schema, group, item);

    if (asked === undefined) {
      return [`${at}/at: ${itemPointer} is not an item the instrument asks`];
    }

    const { groupSchema, itemSchema } = asked;
    const supplementTo = itemSchema[SUPPLEMENT_KEYWORD]?.to;
    const misplaced =
      supplementTo === undefined
        ? []
        : pointerFor([group, supplementTo]) === lastNonSupplement
          ? []
          : [
              `${at}/at: ${itemPointer} is a supplement to ${pointerFor([group, supplementTo])}, so is entered straight after it`,
            ];

    if (supplementTo === undefined) {
      lastNonSupplement = itemPointer;
    }

    const repeated = entered.has(itemPointer)
      ? [`${at}/at: ${itemPointer} already has an entry`]
      : [];

    entered.add(itemPointer);

    const groupRequiredByInstrument = (schema.required ?? []).includes(group);
    const firstClaim = optionalGroupClaims.get(group);

    if (!groupRequiredByInstrument && firstClaim === undefined) {
      optionalGroupClaims.set(group, { at, claimed: entry.groupRequired });
    }

    const disagreeing =
      repeated.length === 0 &&
      firstClaim !== undefined &&
      firstClaim.claimed !== entry.groupRequired
        ? [
            `${at}/groupRequired: "${entry.groupRequired}" contradicts "${firstClaim.claimed}" at ${firstClaim.at}/groupRequired, for the same group`,
          ]
        : [];

    return [
      ...repeated,
      ...misplaced,
      ...requirementIssuesIn(
        `${at}/required`,
        entry.required,
        (groupSchema.required ?? []).includes(item),
      ),
      ...requirementIssuesIn(
        `${at}/groupRequired`,
        entry.groupRequired,
        groupRequiredByInstrument,
      ),
      ...disagreeing,
      ...answerIssuesIn(
        at,
        entry.answers,
        closedValuesOf(itemSchema.properties.response, schema.$defs ?? {}),
        responses.get(itemPointer),
      ),
    ];
  });

  const unentered = toResponseRows(document)
    .map(({ group, item }) => pointerFor([group, item]))
    .filter((answered) => !entered.has(answered))
    .map((answered) => `/items: ${answered} is answered, but has no entry`);

  return [...entryIssues, ...unentered];
};

/**
 * The evidence's shape, then everything `presentationIssuesIn` finds, as one
 * parse: the instrument checks never see a malformed shape, since a transform
 * does not run once the shape is refused, and the brand comes only from
 * passing both.
 */
const attachablePresentationSchema = ({
  schema,
  document,
}: {
  schema: PublishedInstrumentSchema;
  document: SubmissionDocumentUnbranded;
}) =>
  PresentationSchema.transform((presentation, context) => {
    for (const issue of presentationIssuesIn({
      schema,
      document,
      presentation,
    })) {
      context.issues.push({
        code: "custom",
        message: issue,
        input: presentation,
      });
    }

    return presentation;
  }).brand<"haven-common:Presentation">();

/**
 * Evidence the core has checked: shaped as `PresentationSchema` says, and
 * attachable to a submission of the pinned instrument. Only
 * `validatePresentation` makes one, so take this rather than
 * `PresentationUnbranded` wherever evidence must already be checked.
 */
export type Presentation = z.infer<
  ReturnType<typeof attachablePresentationSchema>
>;

/**
 * Evidence as the core accepts it for a submission of the pinned instrument,
 * or a standard error listing every problem with its shape, or else every
 * problem `presentationIssuesIn` finds.
 */
export const validatePresentation = ({
  schema,
  document,
  presentation,
}: {
  schema: PublishedInstrumentSchema;
  document: SubmissionDocumentUnbranded;
  presentation: unknown;
}): Result<Presentation, InvalidError> => {
  const parsed = attachablePresentationSchema({ schema, document }).safeParse(
    presentation,
  );

  return parsed.success
    ? ok(parsed.data)
    : err(
        buildInvalidError({
          message: "The evidence cannot be attached to this instrument",
          // Instrument issues are raised at the root with their pointer in the
          // message; a root shape issue has no pointer to add.
          issues: parsed.error.issues.map(({ path, message }) =>
            path.length === 0 ? message : `${pointerFor(path)}: ${message}`,
          ),
        }),
      );
};
